import {
  Component,
  ChangeDetectorRef,
  type OnInit,
  DestroyRef,
  inject,
  input,
} from '@angular/core';
import { ReactiveFormsModule, type AbstractControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EplusUploadFileService } from '@innogy/feh/services';
import { AutomationIdDirective } from '@innogy/utils-automation-id';
import {
  CrntButton,
  CrntIcon,
  CrntIconButton,
} from '@essent/current-web-angular';
import { FileSizePipe } from '@innogy/utils-deprecated';

import { RfaFormControl } from '../../../level1';
import { focusChangeOptionsToConfig } from '../../helpers/tracker-options-to-config';
import type { RfaComponents } from '../RfaComponents';
import type { RfaFocusChangeTrackerOptions } from '../../models/rfa-tracker-options.interface';
import { getAriaDescribedBy } from '../../helpers/aria-fn-helpers';
import { RfaErrorsComponent } from '../utils/rfa-errors/rfa-errors.component';
import { RfaLabelComponent } from '../utils/rfa-label/rfa-label.component';
import { TrackFieldfocusDirective } from '@analytics/events';

export type RfaFileState = 'loading' | 'error' | 'uploaded';

export interface RfaFile {
  state: RfaFileState;
  name: string;
  originFile: File;
  documentKey?: string;
}

/**
 * @description
 * This component handles all actions needed for uploading files.
 *
 * @usageNotes
 * use this component like any other rfa form control:
 * ```typescript
 * const control = new RfaFormControl(
 *     [], {
 *     label: 'File upload test',
 *     allowedFileTypes: '.pdf',
 *     allowMultiple: false,
 *     maxFiles: 1,
 *     maxFileSize: 15000000,
 *   }, RfaComponents.FileUpload
 * )
 * ```
 *
 * The uploading is done automatically and the value of the control is an array of uploaded files (RfaFile[]).
 * Extract the id of the uploaded file by selecting the documentKey (e.g. control.value[0]?.documentKey)
 */
@Component({
  selector: 'rfa-file-upload',
  standalone: true,
  templateUrl: './rfa-file-upload.component.html',
  imports: [
    ReactiveFormsModule,
    RfaErrorsComponent,
    RfaLabelComponent,
    AutomationIdDirective,
    TrackFieldfocusDirective,
    CrntButton,
    CrntIcon,
    CrntIconButton,
    FileSizePipe,
  ],
  providers: [EplusUploadFileService],
  styleUrls: [
    '../styles.essent.scss',
    '../styles.ed.scss',
    './rfa-file-upload.component.scss',
  ],
})
export class RfaFileUploadComponent implements OnInit {
  readonly control =
    input.required<
      RfaFormControl<
        (typeof RfaComponents.FileUpload)['valueType'],
        typeof RfaComponents.FileUpload
      >
    >();

  private readonly destroyed$ = inject(DestroyRef);
  private readonly fileUploadService = inject(EplusUploadFileService);

  protected readonly defaultMaxFilesErrorMsg =
    'Het maximaal aantal bestanden is overschreden.';
  protected readonly defaultMaxFileSizeErrorMsg =
    'Een van de bestanden is te groot.';
  protected readonly defaultFileErrorErrorMsg =
    'Er is een fout opgetreden tijdens het uploaden van een bestand.';
  protected readonly defaultFileLoadingErrorMsg =
    'Een bestand is nog bezig met uploaden.';

  constructor(private readonly changeRef: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.control().addValidators(this.fileInputValidator);

    const errorMsgs = this.control().options.errorMsgs ?? {};
    errorMsgs['maxFiles'] =
      errorMsgs['maxFiles'] ?? this.defaultMaxFilesErrorMsg;
    errorMsgs['maxFileSize'] =
      errorMsgs['maxFileSize'] ?? this.defaultMaxFileSizeErrorMsg;
    errorMsgs['fileError'] =
      errorMsgs['fileError'] ?? this.defaultFileErrorErrorMsg;
    errorMsgs['fileLoading'] =
      errorMsgs['fileLoading'] ?? this.defaultFileLoadingErrorMsg;
    this.control().options.errorMsgs = errorMsgs;
  }

  get allowMultiple(): boolean {
    return this.control().options.allowMultiple ?? true;
  }

  get allowedFileTypes(): string {
    return this.control().options.allowedFileTypes ?? '*';
  }

  get maxFiles(): number {
    return this.control().options.maxFiles ?? 10;
  }

  get maxFileSize(): number {
    return this.control().options.maxFileSize ?? 5000000;
  }

  focusChangeTrackConfig() {
    const focusChangeOptions: RfaFocusChangeTrackerOptions = this.control()
      .options.analytics?.focusChangeTracker ?? {
      fieldName: this.control().options.label,
    };

    return focusChangeOptionsToConfig(focusChangeOptions, {
      fieldName: this.control().options.label,
      hashValue: false,
    });
  }

  onFileSelect(event: Event) {
    const element = event.target as HTMLInputElement;
    const files = element.files;
    const file = files?.item(0);

    if (file) {
      const controlValue = this.control().value ?? [];
      const rfaFile: RfaFile = {
        originFile: file,
        name: file.name,
        state: 'loading',
      };
      controlValue.push(rfaFile);

      if (rfaFile.originFile.size > this.maxFileSize) {
        rfaFile.state = 'error';
      } else {
        this.uploadFile(rfaFile);
      }
      this.control().setValue(controlValue);
    }
  }

  uploadFile(file: RfaFile) {
    this.fileUploadService
      .call$({
        file: file.originFile,
      })
      .pipe(takeUntilDestroyed(this.destroyed$))
      .subscribe({
        next: (value) => {
          file.state = 'uploaded';
          file.documentKey = value.documentKey;
          this.control().setValue(this.control().value);
          this.control().markAsTouched();
          this.changeRef.detectChanges();
        },
        error: () => {
          file.state = 'error';
          this.control().setValue(this.control().value);
          this.control().markAsTouched();
          this.changeRef.detectChanges();
        },
      });
  }

  removeFile(file: RfaFile) {
    const controlValue = this.control().value;
    controlValue.splice(controlValue.indexOf(file), 1);
    this.control().setValue(controlValue);
  }

  private readonly fileInputValidator: (
    control: AbstractControl
  ) => { [key: string]: any } | null = (control: AbstractControl) => {
    const fileInputControl = control as RfaFormControl<
      (typeof RfaComponents.FileUpload)['valueType'],
      typeof RfaComponents.FileUpload
    >;

    if (fileInputControl.value) {
      const files = fileInputControl.value;

      if (files.length > this.maxFiles) {
        return { maxFiles: true };
      }

      for (const file of files) {
        if (file.originFile.size > this.maxFileSize) {
          return { maxFileSize: true };
        }
        if (file.state === 'error') {
          return { fileError: true };
        }
        if (file.state === 'loading') {
          return { fileLoading: true };
        }
      }
    }
    return null;
  };

  protected ariaDescribedBy() {
    return getAriaDescribedBy(this.control());
  }
}
